import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Trash2, AlignLeft, AlignCenter, AlignRight, Check, Loader2, Type, Minus } from "lucide-react";

// Curated, on-brand typography + palette (matches the app's dark, classy aesthetic).
const FONTS = {
  display: { family: "'Cabinet Grotesk', sans-serif", weight: 800, label: "Display" },
  text: { family: "'Satoshi', sans-serif", weight: 500, label: "Text" },
};
const TEXT_COLORS = ["#FFFFFF", "#0A0A0A", "#14A89A", "#D4AF37", "#E0729B", "#A855F7", "#D4D4D8"];
const SOLIDS = ["#0A0A0A", "#121212", "#14A89A", "#0F3D38", "#3A2A1E", "#2A1E3A", "#D4AF37", "#E0729B"];
const GRADIENTS = [
  { from: "#0A0A0A", to: "#14A89A", angle: 135 },
  { from: "#1A1030", to: "#A855F7", angle: 135 },
  { from: "#2B1A12", to: "#D4AF37", angle: 135 },
  { from: "#2A0F1E", to: "#E0729B", angle: 135 },
  { from: "#0A0A0A", to: "#1F2937", angle: 135 },
];

// Subtle dark scrims to keep text legible over busy photos.
const SCRIMS = [
  { key: "none", label: "None", css: "none" },
  { key: "bottom", label: "Bottom", css: "linear-gradient(to top, rgba(0,0,0,0.62), rgba(0,0,0,0) 55%)" },
  { key: "top", label: "Top", css: "linear-gradient(to bottom, rgba(0,0,0,0.62), rgba(0,0,0,0) 55%)" },
  { key: "even", label: "Even", css: "rgba(0,0,0,0.32)" },
];
function drawScrim(ctx, key, CW, CH) {
  if (key === "even") { ctx.fillStyle = "rgba(0,0,0,0.32)"; ctx.fillRect(0, 0, CW, CH); return; }
  if (key === "bottom" || key === "top") {
    const g = key === "bottom" ? ctx.createLinearGradient(0, CH, 0, 0) : ctx.createLinearGradient(0, 0, 0, CH);
    g.addColorStop(0, "rgba(0,0,0,0.62)");
    g.addColorStop(0.55, "rgba(0,0,0,0)");
    ctx.fillStyle = g; ctx.fillRect(0, 0, CW, CH);
  }
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
let _uid = 0;
const newBox = (over = {}) => ({
  id: ++_uid, text: "Tap to edit", font: "display", color: "#FFFFFF",
  sizePct: 0.075, align: "center", xPct: 0.5, yPct: 0.5, widthPct: 0.82, shadow: false, ...over,
});

const PRESETS = [
  { key: "quote", label: "Quote", bg: { type: "gradient", ...GRADIENTS[1] }, boxes: [
    newBox({ text: "Your words\nbecome your world.", font: "display", color: "#FFFFFF", sizePct: 0.082, yPct: 0.42 }),
    newBox({ text: "- add attribution", font: "text", color: "#D4AF37", sizePct: 0.034, yPct: 0.63, widthPct: 0.7 }),
  ] },
  { key: "statement", label: "Statement", bg: { type: "solid", color: "#0A0A0A" }, boxes: [
    newBox({ text: "Make your\nstatement.", font: "display", color: "#14A89A", sizePct: 0.11, align: "left", yPct: 0.4 }),
  ] },
  { key: "announce", label: "Announce", bg: { type: "gradient", ...GRADIENTS[0] }, boxes: [
    newBox({ text: "ANNOUNCING", font: "text", color: "#D4AF37", sizePct: 0.038, yPct: 0.4 }),
    newBox({ text: "Something new", font: "display", color: "#FFFFFF", sizePct: 0.095, yPct: 0.53, widthPct: 0.86 }),
  ] },
  { key: "minimal", label: "Minimal", bg: { type: "solid", color: "#121212" }, boxes: [
    newBox({ text: "less, but better", font: "text", color: "#FFFFFF", sizePct: 0.05 }),
  ] },
];

function wrapLines(ctx, text, maxWidth) {
  const lines = [];
  for (const para of String(text).split("\n")) {
    const words = para.split(" ");
    let line = "";
    for (const w of words) {
      const test = line ? line + " " + w : w;
      if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
      else line = test;
    }
    lines.push(line);
  }
  return lines;
}

export default function PostDesigner({ mode, imageUrl, mediaType = "image", initialScrim, initialBoxes, onDone, onCancel }) {
  const isMedia = mode !== "text";
  const [bg, setBg] = useState(isMedia ? null : { ...PRESETS[0].bg });
  const [scrim, setScrim] = useState(isMedia ? (initialScrim || "bottom") : "none");
  const [boxes, setBoxes] = useState(() =>
    (initialBoxes && initialBoxes.length)
      ? initialBoxes.map((b) => ({ ...b, id: ++_uid }))
      : (isMedia ? [newBox({ shadow: true })] : PRESETS[0].boxes.map((b) => ({ ...b, id: ++_uid }))));
  const [selId, setSelId] = useState(() => (boxes[0] ? boxes[0].id : null));
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const [imgDims, setImgDims] = useState({ w: 1, h: 1 });
  const [exporting, setExporting] = useState(false);
  const stageRef = useRef(null);
  const imgElRef = useRef(null);
  const dragRef = useRef(null);

  const aspect = isMedia ? imgDims.w / imgDims.h : 1;

  // Load the base photo (for aspect + export).
  useEffect(() => {
    if (!isMedia || !imageUrl) return;
    if (mediaType === "video") {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => setImgDims({ w: v.videoWidth || 1, h: v.videoHeight || 1 });
      v.src = imageUrl;
      return;
    }
    const img = new Image();
    img.onload = () => { imgElRef.current = img; setImgDims({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 }); };
    img.src = imageUrl;
  }, [isMedia, imageUrl, mediaType]);

  // Measure the stage so text scales identically to the export canvas.
  useEffect(() => {
    if (!stageRef.current) return;
    const el = stageRef.current;
    const measure = () => setStage({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [aspect]);

  const sel = boxes.find((b) => b.id === selId) || null;
  const updateBox = (id, patch) => setBoxes((bs) => bs.map((b) => b.id === id ? { ...b, ...patch } : b));
  const updateSel = (patch) => sel && updateBox(sel.id, patch);
  const addBox = () => { if (boxes.length >= 2) return; const b = newBox({ shadow: isMedia, yPct: 0.65 }); setBoxes((bs) => [...bs, b]); setSelId(b.id); };
  const removeBox = (id) => { setBoxes((bs) => bs.filter((b) => b.id !== id)); setSelId((s) => s === id ? null : s); };

  const applyPreset = (p) => { setBg({ ...p.bg }); const nb = p.boxes.map((b) => ({ ...b, id: ++_uid })); setBoxes(nb); setSelId(nb[0] ? nb[0].id : null); };

  const cssBg = useMemo(() => {
    if (isMedia || !bg) return "transparent";
    return bg.type === "gradient" ? `linear-gradient(${bg.angle}deg, ${bg.from}, ${bg.to})` : bg.color;
  }, [isMedia, bg]);

  const onBoxPointerDown = (e, id) => {
    e.stopPropagation();
    setSelId(id);
    const rect = stageRef.current.getBoundingClientRect();
    const b = boxes.find((x) => x.id === id);
    dragRef.current = { id, rect, ox: (e.clientX - rect.left) / rect.width - b.xPct, oy: (e.clientY - rect.top) / rect.height - b.yPct };
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
  };
  const onDragMove = (e) => {
    const d = dragRef.current; if (!d) return;
    const x = (e.clientX - d.rect.left) / d.rect.width - d.ox;
    const y = (e.clientY - d.rect.top) / d.rect.height - d.oy;
    updateBox(d.id, { xPct: clamp(x, 0.06, 0.94), yPct: clamp(y, 0.06, 0.94) });
  };
  const onDragEnd = () => { dragRef.current = null; window.removeEventListener("pointermove", onDragMove); window.removeEventListener("pointerup", onDragEnd); };

  const exportImage = async () => {
    setExporting(true);
    try {
      try { await Promise.all([document.fonts.load("800 100px 'Cabinet Grotesk'"), document.fonts.load("500 100px 'Satoshi'")]); } catch {}
      let CW, CH;
      if (isMedia) {
        const longest = 1440, a = imgDims.w / imgDims.h;
        if (imgDims.w >= imgDims.h) { CW = Math.min(longest, imgDims.w); CH = Math.round(CW / a); }
        else { CH = Math.min(longest, imgDims.h); CW = Math.round(CH * a); }
      } else { CW = 1080; CH = 1080; }
      const canvas = document.createElement("canvas");
      canvas.width = CW; canvas.height = CH;
      const ctx = canvas.getContext("2d");
      if (isMedia && imgElRef.current) ctx.drawImage(imgElRef.current, 0, 0, CW, CH);
      else if (bg && bg.type === "gradient") {
        const rad = (bg.angle * Math.PI) / 180;
        const x = Math.cos(rad), y = Math.sin(rad);
        const g = ctx.createLinearGradient((0.5 - x / 2) * CW, (0.5 - y / 2) * CH, (0.5 + x / 2) * CW, (0.5 + y / 2) * CH);
        g.addColorStop(0, bg.from); g.addColorStop(1, bg.to);
        ctx.fillStyle = g; ctx.fillRect(0, 0, CW, CH);
      } else { ctx.fillStyle = (bg && bg.color) || "#0A0A0A"; ctx.fillRect(0, 0, CW, CH); }
      if (isMedia && scrim !== "none") drawScrim(ctx, scrim, CW, CH);

      for (const box of boxes) {
        const f = FONTS[box.font];
        const fontPx = Math.round(box.sizePct * CH);
        ctx.font = `${f.weight} ${fontPx}px ${f.family}`;
        ctx.fillStyle = box.color;
        ctx.textAlign = box.align;
        ctx.textBaseline = "middle";
        if (box.shadow) { ctx.shadowColor = "rgba(0,0,0,0.55)"; ctx.shadowBlur = fontPx * 0.18; ctx.shadowOffsetY = fontPx * 0.06; }
        else { ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0; }
        const maxW = box.widthPct * CW;
        const lines = wrapLines(ctx, box.text, maxW);
        const lh = fontPx * 1.2;
        const startY = box.yPct * CH - ((lines.length - 1) * lh) / 2;
        const left = box.xPct * CW - maxW / 2;
        const anchorX = box.align === "left" ? left : box.align === "right" ? left + maxW : box.xPct * CW;
        lines.forEach((ln, i) => ctx.fillText(ln, anchorX, startY + i * lh));
      }
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
      const file = new File([blob], "design.jpg", { type: "image/jpeg" });
      onDone(file);
    } finally { setExporting(false); }
  };

  const swatch = (c) => c === "#FFFFFF" ? "#FFFFFF" : c;

  // Media mode (photo/video) returns OVERLAY metadata (text stays a separate,
  // non-burned layer rendered on top of the original media in the feed).
  // Text-post mode still burns text onto a generated background image.
  const handleDone = async () => {
    if (!isMedia) return exportImage();
    const clean = boxes
      .filter((b) => (b.text || "").trim())
      .map(({ id, ...rest }) => rest);
    onDone({ scrim, boxes: clean });
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[#0A0A0A] flex flex-col" data-testid="post-designer">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
        <button onClick={onCancel} data-testid="designer-cancel" className="flex items-center gap-2 text-zinc-400 hover:text-white font-body text-sm"><X size={18} /> Back</button>
        <h3 className="font-heading text-base text-white">{isMedia ? (mediaType === "video" ? "Add text to video" : "Add text to photo") : "Design a post"}</h3>
        <button onClick={handleDone} disabled={exporting} data-testid="designer-done" className="flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] px-4 py-2 font-body text-sm font-medium hover:bg-[#2BC4B5] transition-colors disabled:opacity-50">
          {exporting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Done
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Stage */}
        <div className="flex justify-center p-4">
          <div
            ref={stageRef}
            onPointerDown={() => setSelId(null)}
            data-testid="designer-stage"
            className="relative w-full max-w-[min(78vh,420px)] overflow-hidden select-none touch-none bg-black"
            style={{ aspectRatio: String(aspect), background: cssBg }}
          >
            {isMedia && imageUrl && (mediaType === "video"
              ? <video src={imageUrl} muted loop autoPlay playsInline className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
              : <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />)}
            {isMedia && scrim !== "none" && (
              <div className="absolute inset-0 pointer-events-none" style={{ background: SCRIMS.find((s) => s.key === scrim).css }} />
            )}
            {boxes.map((b) => (
              <div
                key={b.id}
                onPointerDown={(e) => onBoxPointerDown(e, b.id)}
                data-testid={`designer-textbox-${b.id}`}
                className={`absolute cursor-move ${selId === b.id ? "outline-dashed outline-1 outline-white/60" : ""}`}
                style={{
                  left: `${b.xPct * 100}%`, top: `${b.yPct * 100}%`, width: `${b.widthPct * 100}%`,
                  transform: "translate(-50%, -50%)", textAlign: b.align,
                  fontFamily: FONTS[b.font].family, fontWeight: FONTS[b.font].weight,
                  color: b.color, fontSize: stage.h ? `${b.sizePct * stage.h}px` : "24px",
                  lineHeight: 1.2, whiteSpace: "pre-wrap", wordBreak: "break-word",
                  textShadow: b.shadow ? "0 2px 8px rgba(0,0,0,0.55)" : "none",
                  padding: "2px",
                }}
              >{b.text || " "}</div>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="px-4 pb-8 space-y-5 max-w-lg mx-auto">
          {isMedia && (
            <div>
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Legibility scrim</p>
              <div className="flex gap-2 flex-wrap">
                {SCRIMS.map((s) => (
                  <button key={s.key} onClick={() => setScrim(s.key)} data-testid={`scrim-${s.key}`} className={`px-3 py-2 font-body text-xs border ${scrim === s.key ? "border-[#14A89A] text-white" : "border-white/15 text-zinc-400"} hover:text-white transition-colors`}>{s.label}</button>
                ))}
              </div>
              <p className="font-body text-[11px] text-zinc-600 mt-2">A subtle dark gradient behind your text so it stays readable on busy photos.</p>
            </div>
          )}
          {!isMedia && (
            <>
              <div>
                <p className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Presets</p>
                <div className="flex gap-2 flex-wrap">
                  {PRESETS.map((p) => (
                    <button key={p.key} onClick={() => applyPreset(p)} data-testid={`preset-${p.key}`} className="border border-white/15 text-zinc-300 px-3 py-2 font-body text-xs hover:border-[#14A89A] hover:text-white transition-colors">{p.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Background</p>
                <div className="flex gap-2 flex-wrap items-center">
                  {SOLIDS.map((c) => (
                    <button key={c} onClick={() => setBg({ type: "solid", color: c })} data-testid={`bg-solid-${c}`} className={`w-8 h-8 border ${bg && bg.type === "solid" && bg.color === c ? "border-[#14A89A] border-2" : "border-white/20"}`} style={{ background: c }} />
                  ))}
                </div>
                <div className="flex gap-2 flex-wrap items-center mt-2">
                  {GRADIENTS.map((g, i) => (
                    <button key={i} onClick={() => setBg({ type: "gradient", ...g })} data-testid={`bg-gradient-${i}`} className={`w-8 h-8 border ${bg && bg.type === "gradient" && bg.from === g.from && bg.to === g.to ? "border-[#14A89A] border-2" : "border-white/20"}`} style={{ background: `linear-gradient(${g.angle}deg, ${g.from}, ${g.to})` }} />
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex items-center justify-between">
            <p className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500">Text ({boxes.length}/2)</p>
            <button onClick={addBox} disabled={boxes.length >= 2} data-testid="designer-add-text" className="flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-1.5 font-body text-xs hover:border-[#14A89A] hover:text-white transition-colors disabled:opacity-40"><Plus size={14} /> Add text</button>
          </div>

          {sel ? (
            <div className="space-y-4 border border-white/10 p-4 bg-[#121212]">
              <textarea value={sel.text} onChange={(e) => updateSel({ text: e.target.value })} rows={2} data-testid="designer-text-input" placeholder="Type your text…" className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body text-sm resize-none" />

              <div className="flex items-center gap-2 flex-wrap">
                {Object.entries(FONTS).map(([k, f]) => (
                  <button key={k} onClick={() => updateSel({ font: k })} data-testid={`font-${k}`} className={`px-3 py-1.5 font-body text-xs border ${sel.font === k ? "border-[#14A89A] text-white" : "border-white/15 text-zinc-400"}`} style={{ fontFamily: f.family }}>{f.label}</button>
                ))}
                <div className="w-px h-6 bg-white/10 mx-1" />
                {["left", "center", "right"].map((a) => (
                  <button key={a} onClick={() => updateSel({ align: a })} data-testid={`align-${a}`} className={`p-2 border ${sel.align === a ? "border-[#14A89A] text-white" : "border-white/15 text-zinc-400"}`}>
                    {a === "left" ? <AlignLeft size={14} /> : a === "center" ? <AlignCenter size={14} /> : <AlignRight size={14} />}
                  </button>
                ))}
                <div className="w-px h-6 bg-white/10 mx-1" />
                <button onClick={() => updateSel({ sizePct: clamp(sel.sizePct - 0.008, 0.03, 0.2) })} data-testid="size-down" className="p-2 border border-white/15 text-zinc-400 hover:text-white"><Minus size={14} /></button>
                <span className="font-body text-xs text-zinc-500 flex items-center gap-1"><Type size={12} /> {Math.round(sel.sizePct * 100)}</span>
                <button onClick={() => updateSel({ sizePct: clamp(sel.sizePct + 0.008, 0.03, 0.2) })} data-testid="size-up" className="p-2 border border-white/15 text-zinc-400 hover:text-white"><Plus size={14} /></button>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {TEXT_COLORS.map((c) => (
                  <button key={c} onClick={() => updateSel({ color: c })} data-testid={`text-color-${c}`} className={`w-7 h-7 rounded-full border ${sel.color === c ? "border-[#14A89A] border-2" : "border-white/25"}`} style={{ background: swatch(c) }} />
                ))}
                <div className="w-px h-6 bg-white/10 mx-1" />
                <button onClick={() => updateSel({ shadow: !sel.shadow })} data-testid="toggle-shadow" className={`px-3 py-1.5 font-body text-xs border ${sel.shadow ? "border-[#14A89A] text-white" : "border-white/15 text-zinc-400"}`}>Shadow</button>
                <button onClick={() => removeBox(sel.id)} data-testid="designer-remove-text" className="ml-auto p-2 border border-white/15 text-zinc-400 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
              </div>
            </div>
          ) : (
            <p className="font-body text-xs text-zinc-600">Tap a text block to edit it, or add one.</p>
          )}
        </div>
      </div>
    </div>
  );
}
